import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Home, Star, StarHalf } from 'lucide-react';
import { Review } from '../types/review';
import { useReviews } from '../hooks/useReviews';
import ReviewCard from '../components/ReviewCard';
import { trackReviewRead } from '../utils/analytics';

interface PropertyPageProps {
  onReviewClick: () => void;
}

interface RatingBarProps {
  stars: number;
  count: number;
  total: number;
}

interface RatingDisplayProps {
  rating: number;
  label: string;
  showValue?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const RatingBar: React.FC<RatingBarProps> = ({ stars, count, total }) => (
  <div className="flex items-center gap-2">
    <span className="w-8 text-sm">{stars}-star</span>
    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
      <div 
        className="h-full bg-green-500 rounded-full"
        style={{ width: `${total > 0 ? (count / total) * 100 : 0}%` }}
      />
    </div>
    <span className="w-8 text-sm text-right">{count}</span>
  </div>
);

const RatingDisplay: React.FC<RatingDisplayProps> = ({ rating, label, showValue = false, size = 'sm' }) => {
  const circleSize = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const starSize = size === 'lg' ? 'w-4 h-4' : size === 'md' ? 'w-3 h-3' : 'w-2.5 h-2.5';
  
  const getStarColor = (starRating: number) => {
    if (starRating >= 4.2) return '#008d49'; // 5 - dark green
    if (starRating >= 3.4) return '#a1e883'; // 4 - light green  
    if (starRating >= 2.6) return '#ffd830'; // 3 - yellow
    if (starRating >= 1.8) return '#f86306'; // 2 - orange
    return '#ef001d'; // 1 - red
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex-1">
        <div className="flex gap-1 mb-1">
          {[0, 1, 2, 3, 4].map((index) => {
            const filled = rating - index;
            const starColor = getStarColor(rating);
            return (
              <div key={index} className={`${circleSize} rounded-full flex items-center justify-center`}
                   style={{ backgroundColor: filled >= 1 ? starColor : (filled > 0 ? starColor : '#d1d5db') }}>
                {filled >= 1 ? (
                  <Star className={`${starSize} text-white fill-white`} />
                ) : filled > 0 ? (
                  <StarHalf className={`${starSize} text-white fill-white`} />
                ) : (
                  <Star className={`${starSize} text-white fill-white opacity-60`} />
                )}
              </div>
            );
          })}
        </div>
        <div className="text-sm text-gray-600">{label}</div>
      </div>
      {showValue && (
        <div className="text-2xl font-bold">{rating.toFixed(1)}</div>
      )}
    </div>
  );
};

const renderRating = (rating: number) => {
  const getStarColor = (starRating: number) => {
    if (starRating >= 4.2) return '#008d49'; // 5 - dark green
    if (starRating >= 3.4) return '#a1e883'; // 4 - light green  
    if (starRating >= 2.6) return '#ffd830'; // 3 - yellow
    if (starRating >= 1.8) return '#f86306'; // 2 - orange
    return '#ef001d'; // 1 - red
  };

  return (
  <div className="flex gap-1">
    {[0, 1, 2, 3, 4].map((index) => {
      const filled = rating - index;
      const starColor = getStarColor(rating);
      return (
        <div key={index} className="w-5 h-5 rounded-full flex items-center justify-center"
             style={{ backgroundColor: filled >= 1 ? starColor : (filled > 0 ? starColor : '#d1d5db') }}>
          {filled >= 1 ? (
            <Star className="w-3 h-3 text-white fill-white" />
          ) : filled > 0 ? (
            <StarHalf className="w-3 h-3 text-white fill-white" />
          ) : (
            <Star className="w-3 h-3 text-white fill-white opacity-60" />
          )}
        </div>
      );
    })}
  </div>
  );
};

export default function PropertyPage({ onReviewClick }: PropertyPageProps) {
  const { address } = useParams();
  const location = useLocation();
  const decodedAddress = address ? decodeURIComponent(address) : '';
  const searchParams = new URLSearchParams(location.search);
  const udprn = searchParams.get('udprn');
  const { reviews } = useReviews(udprn || undefined);

  // Track review reads when component mounts with reviews
  React.useEffect(() => {
    if (reviews.length > 0) {
      reviews.forEach(review => {
        trackReviewRead(decodedAddress, review.id);
      });
    }
  }, [reviews, decodedAddress]);

  if (reviews.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50">
        <header className="bg-black">
          <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center">
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center">
                <img
                  src="https://rentreview.io/wp-content/uploads/2025/05/Rent-Review-Logo.png"
                  alt="RentReview Logo"
                  className="h-12"
                />
              </Link>
              <Link to="/leave-review" className="text-sm text-white">Leave a Review</Link>
            </div>
          </nav>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-8">
            <Home className="w-8 h-8" />
            <h1 className="text-3xl font-bold">{decodedAddress}</h1>
          </div>

          <p className="mb-8">There are no reviews for this property yet.</p>
          <h2 className="text-xl font-bold mb-10">Help future tenants by writing the first review.</h2>

          <button
            onClick={onReviewClick}
            className="bg-[#30a86b] text-white px-6 py-3 rounded-md font-medium hover:bg-[#2a9760] shadow-sm flex items-center gap-2 mb-12"
          >
            <span className="inline-block">✍️</span>
            Leave a Review
          </button>

          <div className="flex gap-12">
            <div className="bg-[#edae6d] rounded-2xl p-8 pr-4 pt-12 w-[400px]">
              <h2 className="text-black text-4xl font-extrabold mb-8">
                Changing Renting,<br />
                One Review<br />
                at a Time
              </h2>
              <div className="space-y-4 mt-8">
                <div className="flex items-center gap-2.5 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/19.png" alt="Damp" className="w-5 h-5 object-contain" />
                  <span className="text-base font-bold">Damp and Mould</span>
                </div>
                <div className="flex items-center gap-2.5 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/20.png" alt="Deposit" className="w-5 h-5 object-contain" />
                  <span className="text-base font-bold">Unreturned Deposits</span>
                </div>
                <div className="flex items-center gap-2.5 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/21.png" alt="Ghost" className="w-5 h-5 object-contain" />
                  <span className="text-base font-bold">Ghosting Letting Agents</span>
                </div>
                <div className="flex items-center gap-2.5 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/22.png" alt="Rat" className="w-5 h-5 object-contain" />
                  <span className="text-base font-bold">Rodents and Pests</span>
                </div>
                <div className="flex items-center gap-2.5 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/24.png" alt="Boiler" className="w-5 h-5 object-contain" />
                  <span className="text-base font-bold">Broken Boilers in Winter</span>
                </div>
                <div className="flex items-center gap-2.5 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/23.png" alt="Unlivable" className="w-5 h-5 object-contain" />
                  <span className="text-base font-bold">Unlivable Conditions</span>
                </div>
                <div className="flex items-center gap-2.5 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/25.png" alt="Noise" className="w-5 h-5 object-contain" />
                  <span className="text-base font-bold">Noisy Neighbours or Party Flats</span>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <iframe
                width="100%"
                height="400"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(decodedAddress)}&zoom=15`}
                className="w-full h-[400px] rounded-lg shadow-md border-0"
                title={`Map of ${decodedAddress}`}
              ></iframe>
            </div>
          </div>
        </main>
        
        <footer className="mt-8">
          <div className="max-w-7xl mx-auto px-4 pb-4">
            {udprn && (
              <div className="text-xs text-gray-400 text-center">
                UDPRN: {udprn}
              </div>
            )}
          </div>
        </footer>
      </div>
    );
  }

  const calculateAverageRating = (key: keyof Pick<Review, 'property_rating' | 'agent_rating' | 'landlord_rating' | 'location_rating'>) => {
    const validRatings = reviews.filter(review => typeof review[key] === 'number' && review[key] > 0);
    return validRatings.length > 0
      ? validRatings.reduce((acc, review) => acc + (review[key] as number), 0) / validRatings.length
      : 0;
  };

  const averagePropertyRating = calculateAverageRating('property_rating');
  const averageAgentRating = calculateAverageRating('agent_rating');
  const averageLandlordRating = calculateAverageRating('landlord_rating');
  const averageLocationRating = calculateAverageRating('location_rating');

  // Calculate overall rating only including ratings that exist (> 0)
  const ratingsToInclude = [
    averagePropertyRating,
    averageLocationRating,
    ...(averageAgentRating > 0 ? [averageAgentRating] : []),
    ...(averageLandlordRating > 0 ? [averageLandlordRating] : [])
  ];

  const overallRating = (
    ratingsToInclude.reduce((sum, rating) => sum + rating, 0) / ratingsToInclude.length
  );


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50">
      <header className="bg-black text-white">
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center">
              <img
                src="https://rentreview.io/wp-content/uploads/2025/05/Rent-Review-Logo.png"
                alt="RentReview Logo"
                className="h-12"
              />
            </Link>
            <Link to="/leave-review" className="text-sm">Leave a Review</Link>
          </div>
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Property Title */}
          <div className="flex items-start gap-3 mb-4">
            <Home className="w-8 h-8 flex-shrink-0" />
            <h1 className="text-2xl font-bold break-words">{decodedAddress}</h1>
          </div>
          
          {/* Review Count and Stars */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium">{reviews.length} Reviews</span>
            {renderRating(overallRating)}
          </div>

          {/* Leave Review Button - Centered */}
          <div className="flex justify-center mb-6">
            <button
              onClick={onReviewClick}
              className="bg-[#30a86b] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#2a9760] shadow-sm flex items-center gap-2"
            >
              <span className="inline-block">✍️</span>
              Leave a Review
            </button>
          </div>

          {/* Overall Rating and Map Side by Side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Overall Rating Box */}
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-center mb-4">
                <div className="text-3xl font-bold mb-1">{overallRating.toFixed(1)}</div>
                <div className="text-sm text-gray-600 mb-2">Overall rating</div>
                <div className="flex justify-center mb-2">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((index) => {
                      const filled = overallRating - index;
                      const getStarColor = (starRating: number) => {
                        if (starRating >= 4.2) return '#008d49';
                        if (starRating >= 3.4) return '#a1e883';
                        if (starRating >= 2.6) return '#ffd830';
                        if (starRating >= 1.8) return '#f86306';
                        return '#ef001d';
                      };
                      const starColor = getStarColor(overallRating);
                      return (
                        <div key={index} className="w-6 h-6 rounded-full flex items-center justify-center"
                             style={{ backgroundColor: filled >= 1 ? starColor : (filled > 0 ? starColor : '#d1d5db') }}>
                          {filled >= 1 ? (
                            <Star className="w-4 h-4 text-white fill-white" />
                          ) : filled > 0 ? (
                            <StarHalf className="w-4 h-4 text-white fill-white" />
                          ) : (
                            <Star className="w-4 h-4 text-white fill-white opacity-60" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <RatingDisplay 
                  rating={averagePropertyRating} 
                  label="Property Rating" 
                  showValue={true}
                  size="sm"
                />
                <RatingDisplay 
                  rating={averageLocationRating} 
                  label="Location Rating" 
                  showValue={true}
                  size="sm"
                />
                <RatingDisplay 
                  rating={averageAgentRating} 
                  label="Letting Agent Rating" 
                  showValue={true}
                  size="sm"
                />
                {reviews.some(review => review.landlord_rating !== undefined) && (
                  <RatingDisplay 
                    rating={averageLandlordRating} 
                    label="Landlord Rating" 
                    showValue={true}
                    size="sm"
                  />
                )}
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <iframe
                width="100%"
                height="300"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(decodedAddress)}&zoom=15`}
                className="w-full h-[300px] border-0"
                title={`Map of ${decodedAddress}`}
              ></iframe>
            </div>
          </div>

          {/* Reviews Section */}
          <h2 className="text-xl font-semibold mb-4">Check what previous tenants say about this property</h2>
          <div className="space-y-4">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} address={decodedAddress} />
            ))}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex lg:gap-8">
          <div className="flex-1 min-w-0 max-w-4xl">
            <div className="flex items-start gap-3 mb-4 min-w-0">
              <Home className="w-8 h-8" />
              <h1 className="text-3xl font-bold break-words min-w-0 flex-1 max-w-full overflow-wrap-anywhere">{decodedAddress}</h1>
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm">{reviews.length} Reviews</span>
              {renderRating(overallRating)}
            </div>

            <button
              onClick={onReviewClick}
              className="bg-[#30a86b] text-white px-6 py-3 rounded-md font-medium hover:bg-[#2a9760] shadow-sm flex items-center gap-2"
            >
              <span className="inline-block">✍️</span>
              Leave a Review
            </button>

            <h2 className="text-xl font-semibold mb-4 mt-8">Check what previous tenants say about this property</h2>
            <div className="space-y-4 max-w-full">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} address={decodedAddress} />
              ))}
            </div>
          </div>

          {/* Desktop: Overall rating box on the right */}
          <div className="w-full lg:w-80 lg:shrink-0">
            <div className="bg-white p-6 rounded-lg border space-y-6">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-4 mb-2">
                  <div className="text-5xl font-bold">{overallRating.toFixed(1)}</div>
                  <div className="text-lg text-gray-600">Overall rating</div>
                </div>
                <div className="flex justify-center mb-1">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((index) => {
                      const filled = overallRating - index;
                      const getStarColor = (starRating: number) => {
                        if (starRating >= 4.2) return '#008d49';
                        if (starRating >= 3.4) return '#a1e883';
                        if (starRating >= 2.6) return '#ffd830';
                        if (starRating >= 1.8) return '#f86306';
                        return '#ef001d';
                      };
                      const starColor = getStarColor(overallRating);
                      return (
                        <div key={index} className="w-8 h-8 rounded-full flex items-center justify-center"
                             style={{ backgroundColor: filled >= 1 ? starColor : (filled > 0 ? starColor : '#d1d5db') }}>
                          {filled >= 1 ? (
                            <Star className="w-5 h-5 text-white fill-white" />
                          ) : filled > 0 ? (
                            <StarHalf className="w-5 h-5 text-white fill-white" />
                          ) : (
                            <Star className="w-5 h-5 text-white fill-white opacity-60" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="text-sm text-gray-600">{reviews.length} reviews</div>
              </div>

              <div className="space-y-4">
                <RatingDisplay 
                  rating={averagePropertyRating} 
                  label="Property Rating" 
                  showValue={true}
                  size="md"
                />
                <RatingDisplay 
                  rating={averageLocationRating} 
                  label="Location Rating" 
                  showValue={true}
                  size="md"
                />
                <RatingDisplay 
                  rating={averageAgentRating} 
                  label="Letting Agent Rating" 
                  showValue={true}
                  size="md"
                />
                {reviews.some(review => review.landlord_rating !== undefined) && (
                  <RatingDisplay 
                    rating={averageLandlordRating} 
                    label="Landlord Rating" 
                    showValue={true}
                    size="md"
                  />
                )}
              </div>
            </div>
            
            {/* Desktop map - in sidebar */}
            <div className="mt-6">
              <iframe
                width="100%"
                height="400"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(decodedAddress)}&zoom=15`}
                className="w-full h-[400px] rounded-lg shadow-md border-0"
                title={`Map of ${decodedAddress}`}
              ></iframe>
            </div>
          </div>
        </div>
      </main>
      
      <footer className="mt-8">
        <div className="max-w-7xl mx-auto px-4 pb-4">
          {udprn && (
            <div className="text-xs text-gray-400 text-center">
              UDPRN: {udprn}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}